/**
 * 백엔드 SSE 이벤트 타입 — **계약의 프런트측 표현**.
 *
 * 정본은 백엔드 `chat_service.py` 이고, 이 파일은 그것을 읽는 쪽의 타입이다. 계약 스냅샷은
 * `../../../tests/fixtures/sse/*.json`(백엔드 실행 결과)이며 리듀서 유닛이 그 파일을 직접 읽는다.
 *
 * ⚠ **7종이다.** 계약 문서(10b §2)는 오래도록 6종으로 적고 "keepalive 없음"이라 단언했지만
 * `ping` 이 실재한다(2026-07-20 장주기 도구 무프레임 사고의 대책). F-0 에서 문서를 정정했다.
 * `ping` 을 지우거나 진행 이벤트로 바꾸면 그 사고가 재발한다.
 */

export type RunEventType = 'thread' | 'text' | 'tool' | 'gate' | 'done' | 'error' | 'ping'

export interface RunEvent {
  type: string // 미지의 타입도 받는다 — 리듀서가 무시하되 죽지 않아야 한다
  data: Record<string, unknown>
}

/** 도구 호출 1건 — `phase` 로 호출/결과를 구분한다(같은 `id` 로 이어진다). */
export interface ToolPart {
  kind: 'tool'
  callId: string
  name: string
  /** 사람이 읽는 이름. 없으면 도구명 그대로(식별자 노출보다 낫다). */
  label: string
  phase: 'call' | 'result'
  args?: Record<string, unknown>
  content?: string
}

export interface TextPart {
  kind: 'text'
  id: string
  text: string
}

export type AssistantPart = TextPart | ToolPart

/** HITL 게이트 — **휘발**이다. 이력에 죽은 버튼을 남기지 않는다(승인/반려 후 사라진다). */
export interface GateRequest {
  gateType: string
  question: string
  order: Record<string, unknown> | null
  threadId: string | null
}

export interface UserMessage {
  role: 'user'
  id: string
  text: string
}

export interface AssistantMessage {
  role: 'assistant'
  id: string
  parts: AssistantPart[]
}

export type ChatMessage = UserMessage | AssistantMessage

/**
 * 실행 상태.
 *
 * `awaiting_gate` 를 `done` 과 구분하는 것이 이 타입의 존재 이유다: 게이트가 뜨면 백엔드는
 * **`done` 을 보내지 않는다**. 둘을 같게 다루면 유일한 비가역 조작(발주)이 조용히 사라지거나
 * 영원히 대기한다.
 */
export type RunStatus = 'idle' | 'streaming' | 'awaiting_gate' | 'done' | 'error'

export interface RunState {
  threadId: string | null
  messages: ChatMessage[]
  gate: GateRequest | null
  status: RunStatus
  error: string | null
  /** 마지막 하트비트 수신 순번 — `ping` 은 무시하되 "살아 있다"는 사실은 화면이 쓸 수 있다. */
  pings: number
}

export const initialRunState: RunState = {
  threadId: null,
  messages: [],
  gate: null,
  status: 'idle',
  error: null,
  pings: 0,
}
